import { InfoPage } from '@/components/info-page';
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { Badge } from '@/components/ui/badge';
import { EmptyArray, getOrgs } from '@/lib/api-calls';
import { useErrorToast } from '@/lib/error-handling';
import { paginationRequestSchema } from '@/lib/pagination';
import { validateRouteRole } from '@/lib/routing';
import { isAirAdmin } from '@airreading/common';
import { CreateOrg } from '@/routes/_layout/orgs/-components/create-org';
import { OrgWithSchoolsAndStats, VideoConferenceTypeLabels, videoConferenceTypeSchema } from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';
import { ImportFromEdlink } from './-components/import-from-edlink';
import { schoolTabs } from './schools/-components/tabs';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { usePaginatedFilters } from '@/lib/filtering';
import { z } from 'zod';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';
import { ImportOrgFromV1 } from './-components/import-org-chart-from-v1';

const orgsSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional(),
  meetingType: z.array(videoConferenceTypeSchema.optional()).optional(),
});

export const Route = createFileRoute('/_layout/orgs/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: OrgsIndex,
  validateSearch: orgsSearchSchema,
});

const columnHelper = createColumnHelper<OrgWithSchoolsAndStats>();
const columns = [
  columnHelper.accessor((row) => row, {
    header: 'Name',
    id: 'org-name',
    cell: ({ getValue }) => (
      <Link to={'/orgs/$orgId'} params={{ orgId: getValue().id }}>
        {getValue().name}
      </Link>
    ),
  }),
  columnHelper.accessor('timeZone', {
    header: 'Time Zone',
  }),
  columnHelper.accessor('startPlainDate', {
    header: 'Start Date',
    id: 'startPlainDate',
    cell: ({ getValue }) => {
      const startDate = getValue();

      if (!startDate) return null;
      return <div className="min-w-[90px]">{startDate.toString()}</div>;
    },
  }),
  columnHelper.accessor('endPlainDate', {
    header: 'End Date',
    id: 'endPlainDate',
    cell: ({ getValue }) => {
      const endDate = getValue();

      if (!endDate) return null;
      return <div className="min-w-[90px]">{endDate.toString()}</div>;
    },
  }),
  columnHelper.accessor('totalEnrolledStudents', {
    header: 'Total Enrolled Students',
  }),
  columnHelper.accessor('videoConferenceType', {
    header: 'Meeting Type',
    id: 'video-conference-type',
    cell: ({ getValue }) => VideoConferenceTypeLabels[getValue()],
  }),
  columnHelper.accessor('schools', {
    header: 'Schools',
    id: 'customer-school',
    cell: ({ getValue }) => (
      <div className="flex flex-col gap-y-1">
        {(getValue() || EmptyArray).map((school) => (
          <Link to="/orgs/schools/$schoolId/schedules" params={{ schoolId: school.id }} key={school.id}>
            <Badge variant={'outline'} key={school.id} className="hover:bg-accent">
              {school.name}
            </Badge>
          </Link>
        ))}
      </div>
    ),
  }),
];

function OrgsIndex() {
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      meetingType: [[meetingType, activeMeetingType], setMeetingType],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'meetingType']);

  const {
    data: response,
    error,
    refetch,
  } = useQuery(
    getOrgs({
      pageIndex,
      pageSize,
      filters: { query: activeQuery, meetingType: activeMeetingType },
    }),
  );
  const orgs = useMemo(() => response?.orgs || EmptyArray, [response]);
  useErrorToast(error, 'Error loading districts. Please try again.');

  const table = useReactTable({
    data: orgs,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    defaultColumn: {
      enableSorting: false,
    },
    rowCount: response?.total || 0,
  });
  return (
    <InfoPage headerText="Schools" tabs={schoolTabs()}>
      <EntityTable
        table={table}
        hasError={!!error}
        errorState={<TableErrorState refetchAction={refetch} />}
        showPagination={true}
        topBarRightComponents={
          <>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button className="ml-auto hidden h-8 lg:flex">
                  Add District
                  <ChevronDown className="mr-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[150px]">
                <CreateOrg
                  trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Add District</DropdownMenuItem>}
                />
                <ImportOrgFromV1 />
                <ImportFromEdlink
                  trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Import from Edlink</DropdownMenuItem>}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
        filters={{
          filterLeftComponents: (
            <>
              <TableFilterInputField
                placeholder="Search district by name"
                value={query ?? ''}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
              />
              <TableFilterDropdown
                title="Meeting Type"
                options={videoConferenceTypeSchema.options.map((value) => ({
                  label: VideoConferenceTypeLabels[value],
                  value,
                }))}
                selectedValues={meetingType || []}
                onChange={setMeetingType}
              />
              <TableFilterResetButton show onClick={resetFilters} />
            </>
          ),
        }}
      />
    </InfoPage>
  );
}
